import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { useFormat } from "@/lib/format";
import { articleName, fetchArticle, fetchArticleMovements, formatPrice } from "@/services/articles";
import type { Movement } from "@/services/stockLedger";

const WH_NAMES: Record<number, string> = { 1: "Penthalaz", 67: "Dielsdorf", 4: "Berlin", 34: "Hackbridge" };

// His "Fiche article" (Z Production `openItemCard`): stock, references, purchase terms for managers,
// the remark, and the article's stock history.
export default function ArticleSheetDialog({ reference, warehouseId, onClose }: {
  reference: string; warehouseId: number | null; onClose: () => void;
}) {
  const { t, locale } = useT();
  const [history, setHistory] = useState(false);
  const q = useQuery({ queryKey: ["article", reference, warehouseId], queryFn: () => fetchArticle(reference, warehouseId) });
  const moves = useQuery({
    queryKey: ["article-moves", reference, warehouseId], queryFn: () => fetchArticleMovements(reference, warehouseId), enabled: history,
  });
  const a = q.data;
  const name = articleName(reference, locale) ?? a?.references.system_name ?? reference;
  const refs = a?.references;
  const p = a?.purchase;

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-scrim/40 p-4">
      <div onClick={(e) => e.stopPropagation()} className="mt-10 w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-slate-900">{name} <span className="font-mono text-sm text-slate-500">({reference})</span></h2>
            {a?.qty != null && (
              <p className="text-sm text-slate-600">
                {t("sheet.stockNow")} <b>{a.qty}</b>
                {a.kits_possible != null && ` — ${t("sheet.kitsPossible").replace("{n}", String(a.kits_possible))}`}
              </p>
            )}
          </div>
          <button onClick={onClose} aria-label="✕" className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="space-y-4 px-5 py-4 text-sm">
          {q.isLoading && <p className="text-slate-400">{t("loading")}</p>}
          {refs && (
            <Section title={t("sheet.references")} rows={[
              [t("sheet.refZ"), reference, true],
              [t("sheet.refSystem"), refs.myotaku_sku, true],
              [t("sheet.systemName"), refs.system_name],
              [t("sheet.supplier"), refs.supplier],
              [t("sheet.supplierRef"), refs.supplier_sku, true],
              [t("sheet.supplierDesignation"), refs.supplier_designation],
              ["EAN", refs.ean, true],
              [t("sheet.onNotes"), refs.on_delivery_notes.map((r) => `${r.reference}${r.supplier ? ` (${r.supplier})` : ""}`).join(" · ")],
            ]} />
          )}
          {p && (
            <Section title={t("sheet.purchase")} rows={[
              [t("sheet.price"), formatPrice(p.price, p.currency) || t("sheet.noPrice"), false, p.price_note],
              [t("sheet.moq"), p.moq],
              [t("sheet.rules"), p.supplier_rules],
              [t("sheet.usualQty"), p.usual_qty],
            ]} />
          )}
          {p?.remark && (
            <p className={`rounded-lg px-3 py-2 text-xs ${p.to_confirm ? "bg-amber-50 text-amber-900 ring-1 ring-amber-200" : "bg-slate-50 text-slate-700"}`}>
              {p.to_confirm ? `⚠ ${t("sheet.toConfirm")} — ` : `${t("sheet.remark")} — `}{p.remark.replace(/^\s*[aà] confirmer\s*:\s*/i, "")}
            </p>
          )}
          {a && !a.in_catalogue && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {a.purchase_hidden ? t("sheet.reserved") : a.catalogue_imported ? t("sheet.notInCatalogue") : t("sheet.notImported")}
            </p>
          )}
          {a && a.in_catalogue && a.purchase_hidden && <p className="text-xs text-slate-500">{t("sheet.reserved")}</p>}

          <button onClick={() => setHistory(!history)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm">📜 {t("sheet.history")}</button>
          {history && <ArticleHistory moves={moves.data?.movements} loading={moves.isPending} />}
        </div>
      </div>
    </div>
  );
}

// His `section`: a titled list where empty rows are left out.
function Section({ title, rows }: { title: string; rows: [string, string | null | undefined, boolean?, (string | null)?][] }) {
  const shown = rows.filter(([, v]) => v != null && String(v).trim() !== "");
  if (!shown.length) return null;
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
      <dl className="grid grid-cols-[11rem_1fr] gap-x-3 gap-y-1">
        {shown.map(([label, value, mono, note]) => (
          <div key={label} className="contents">
            <dt className="text-slate-500">{label}</dt>
            <dd className={mono ? "font-mono text-slate-800" : "text-slate-800"}>
              {value}{note && <span className="block text-xs text-slate-500">{note}</span>}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// His `openHistory` table for an article: date, the movement with its detail (which order, supplier,
// receipt, client, by whom), + / −, and the stock before → after. Ticks are already merged server-side.
function ArticleHistory({ moves, loading }: { moves: Movement[] | undefined; loading: boolean }) {
  const { t } = useT();
  const { zurichDateTime } = useFormat();
  if (loading) return <p className="text-xs text-slate-400">{t("sheet.hist.loading")}</p>;
  if (!moves?.length) return <p className="text-xs text-slate-400">{t("sheet.noHistory")}</p>;
  const label = (m: Movement) =>
    m.source === "delivery" && m.client_name ? t("sheet.src.deliveryTo", { c: m.client_name }) : t(`sheet.src.${m.source}`);
  const detail = (m: Movement) => [
    m.order_kit ? `${t("sheet.det.order", { k: m.order_kit, n: m.order_qty ?? "" })}${m.source === "production" && m.delta > 0 ? ` ${t("sheet.det.unticked")}` : ""}` : null,
    m.supplier ? t("sheet.det.supplier", { s: m.supplier }) : null,
    m.receipt_id ? t("sheet.det.receipt", { n: m.receipt_id }) : null,
    m.contract_id ? t("sheet.det.contract", { n: m.contract_id }) : null,
    m.from_warehouse_id && m.to_warehouse_id ? `${WH_NAMES[m.from_warehouse_id] ?? `#${m.from_warehouse_id}`} → ${WH_NAMES[m.to_warehouse_id] ?? `#${m.to_warehouse_id}`}` : null,
    m.shortfall ? t("sheet.det.short", { n: m.shortfall }) : null,
    m.by_name ? t("sheet.det.by", { w: m.by_name }) : null,
  ].filter(Boolean).join(" — ");
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-slate-500">
        <tr><th className="py-1 font-medium">{t("sheet.hist.date")}</th><th className="py-1 font-medium">{t("sheet.hist.move")}</th>
          <th className="py-1 text-right font-medium">+ / −</th><th className="py-1 text-right font-medium">{t("sheet.hist.stock")}</th></tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {moves.map((m) => (
          <tr key={m.id} className="align-top">
            <td className="whitespace-nowrap py-1.5 pr-2 text-slate-500">{zurichDateTime(m.at)}</td>
            <td className="py-1.5 pr-2 text-slate-800">{label(m)}{detail(m) && <small className="block text-slate-500">{detail(m)}</small>}</td>
            <td className={`py-1.5 pr-2 text-right font-semibold tabular-nums ${m.delta > 0 ? "text-emerald-700" : m.delta < 0 ? "text-red-700" : "text-slate-500"}`}>
              {m.delta > 0 ? `+${m.delta}` : m.delta < 0 ? `−${-m.delta}` : "="}
            </td>
            <td className="whitespace-nowrap py-1.5 text-right tabular-nums text-slate-700">{m.previous_qty} → {m.qty}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
